import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AddPetModal from "../components/AddPetModal";
import EditPetModal from "../components/EditPetModal";
import PetDetailsModal from "../components/PetDetailsModal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import PetListSection from "../components/PetListSection";
import { API_BASE_URL } from "../config/api";
import "../styles/dashboard-theme.css";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, LayoutDashboard, Loader2, Mail, MapPin, PawPrint, Plus, Share2, Tag, Phone } from "lucide-react";
import { RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { PRICING } from "@/config/pricing";

const Dashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  const [user, setUser] = useState({
    _id: "",
    name: "",
    surname: "",
    email: "",
    isAdmin: false,
    membershipActive: false,
    contact: "",
    membershipStartDate: "",
    address: {
      street: "",
      city: "",
      province: "",
      postalCode: "",
      country: "",
    },
  });

  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [currentPet, setCurrentPet] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    species: "",
    breed: "",
    age: "",
    gender: "",
    dateOfBirth: "",
    photoUrl: "",
    color: "",
    size: "",
    weight: "",
    spayedNeutered: "",
    microchipNumber: "",
  });

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [petToDelete, setPetToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userData = response.data.user;

      setUser({
        _id: userData._id || "",
        name: userData.name?.trim() || "",
        surname: userData.surname?.trim() || "",
        email: userData.email || "",
        isAdmin: userData.isAdmin || false,
        membershipActive: userData.membershipActive || false,
        membershipStartDate: userData.membershipStartDate ?? "",
        contact: userData.contact || "",
        address: userData.address || {
          street: "",
          city: "",
          province: "",
          postalCode: "",
          country: "",
        },
      });
    } catch (error) {
      console.error("Failed to fetch user info:", error);
      toast.error("Failed to load user information.");
    } finally {
      setUserLoading(false);
    }
  }, [token]);


  const fetchPets = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/pets`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPets(response.data);
    } catch (error) {
      console.error("Failed to fetch pets:", error);
      toast.error("Could not load pets.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const refreshPets = () => fetchPets();

  const handleOpenModal = () => {
    setIsEditMode(false);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleViewDetails = async (pet) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/pets/${pet._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedPet(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error("Failed to fetch pet details:", error);
      toast.error("Could not load pet details.");
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedPet(null);
  };

  const handleEditClick = (pet) => {
    setIsEditMode(true);
    setCurrentPet(pet);
    setFormData({
      name: pet.name || "",
      species: pet.species || "",
      breed: pet.breed || "",
      age: pet.age || "",
      gender: pet.gender || "",
      dateOfBirth: pet.dateOfBirth || "",
      photoUrl: pet.photoUrl || "",
      color: pet.color || "",
      size: pet.size || "",
      weight: pet.weight || "",
      spayedNeutered: pet.spayedNeutered?.toString() || "",
      microchipNumber: pet.microchipNumber || "",
    });
    setShowModal(true);
  };

  const handleSaveChanges = async () => {
    try {
      const updatedData = {
        ...formData,
        spayedNeutered: formData.spayedNeutered === "true",
      };

      await axios.put(
        `${API_BASE_URL}/api/pets/${currentPet._id}`,
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Pet updated successfully!");
      setShowModal(false);
      fetchPets();
    } catch (error) {
      console.error("Failed to update pet:", error);
      toast.error("Failed to update pet.");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleDeleteClick = (petId) => {
    setPetToDelete(petId);
    setShowDeleteModal(true);
  };
  

  const confirmDeletePet = async () => {
    if (!petToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/api/pets/${petToDelete}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPets((prev) => prev.filter((pet) => pet._id !== petToDelete));
      toast.success("Pet deleted successfully.");
    } catch (error) {
      console.error("Error deleting pet:", error);
      toast.error("Failed to delete pet.");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setPetToDelete(null);
    }
  };

  const handleShare = () => {
    const message = encodeURIComponent(
      "Check out Found Your Pet — a simple, smart way to help lost pets get home faster. https://www.foundyourpet.co.za/"
    );
    const url = `https://wa.me/?text=${message}`;
    window.open(url, "_blank");
  };

  useEffect(() => {
    if (token) {
      fetchUser();
      fetchPets();
    } else {
      navigate("/login");
    }
  }, [token, fetchUser, fetchPets, navigate]);

  useEffect(() => {
    if (!token) return undefined;

    const openStatuses = new Set(["unfulfilled", "processing", "submitted", "shipped"]);
    const hasOpenTagOrder = pets.some((pet) => {
      const status = pet?.tagOrder?.fulfillment?.status || (pet?.tagOrder ? "unfulfilled" : null);
      if (!status) return false;
      return openStatuses.has(String(status).toLowerCase());
    });

    if (!hasOpenTagOrder) return undefined;

    const interval = setInterval(() => {
      fetchPets();
    }, 20000);

    return () => clearInterval(interval);
  }, [token, pets, fetchPets]);

  const normalizeUserId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") return value._id || "";
    return "";
  };

  const visiblePets = pets.filter((pet) => {
    const currentUserId = normalizeUserId(user?._id);
    const petUserId = normalizeUserId(pet?.userId);
    if (!currentUserId) return true;
    if (!petUserId) return false;
    return petUserId === currentUserId;
  });

  const dogs = visiblePets.filter((pet) => pet.species?.toLowerCase() === "dog");
  const cats = visiblePets.filter((pet) => pet.species?.toLowerCase() === "cat");
  const subscribedPets = visiblePets.filter((pet) => pet.hasMembership);
  const canOrderTags = visiblePets.length > 0;

  const initials = (() => {
    const first = (user?.name || "").trim();
    const last = (user?.surname || "").trim();
    const a = first ? first[0] : "";
    const b = last ? last[0] : "";
    return (a + b).toUpperCase() || "U";
  })();

  const locationLine = (() => {
    const city = user?.address?.city?.trim();
    const province = user?.address?.province?.trim();
    const country = user?.address?.country?.trim();
    return [city, province, country].filter(Boolean).join(", ");
  })();

  return (
    <main className="dashboard-theme relative min-h-screen bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.20),transparent_60%)]" />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_bottom_right,hsl(var(--secondary)/0.70),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-background via-background to-background/50" />

      <div className="mx-auto w-full max-w-6xl px-4 py-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl border bg-card/70 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/60 ring-1 ring-primary/20">
              <LayoutDashboard className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
              <p className="text-sm text-muted-foreground">
                Manage your account and your pets in one place.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
            <Button
              onClick={handleOpenModal}
              title="Add a new pet"
              className="col-span-2 gap-2 shadow-sm hover:shadow sm:col-span-1"
            >
              <Plus className="h-4 w-4" />
              Add pet
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                fetchUser();
                fetchPets();
              }}
              title="Refresh dashboard"
              className="gap-2 bg-card/60 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/50 hover:bg-card/80"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>

            <Button variant="secondary" onClick={handleShare} className="gap-2 shadow-sm hover:shadow">
              <Share2 className="h-4 w-4" />
              Share
            </Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
          <div className="space-y-6">
            <Card className="border-primary/10 bg-card/70 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/60">
              <CardHeader className="space-y-4">
                {userLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-xl font-semibold text-primary ring-1 ring-primary/20">
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <CardTitle className="truncate text-xl">
                          {user.name} {user.surname}
                        </CardTitle>
                        {locationLine ? (
                          <CardDescription className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            <span className="truncate">{locationLine}</span>
                          </CardDescription>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Badge
                        className={cn(
                          user.membershipActive
                            ? "border-emerald-300/70 bg-emerald-100 text-emerald-950 hover:bg-emerald-100 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-200 dark:hover:bg-emerald-500/15"
                            : "border-yellow-300/80 bg-yellow-100 text-yellow-950 hover:bg-yellow-100 dark:border-yellow-500/40 dark:bg-yellow-500/15 dark:text-yellow-200 dark:hover:bg-yellow-500/15"
                        )}
                      >
                        {user.membershipActive ? "Membership active" : "No active membership"}
                      </Badge>
                      <Badge variant="outline">
                        Subscribed pets: {subscribedPets.length} / {visiblePets.length}
                      </Badge>
                    </div>
                  </>
                )}
              </CardHeader>

              {!userLoading ? (
                <CardContent className="space-y-4">
                  <Separator />

                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <span className="truncate text-foreground">{user.email}</span>
                    </div>
                    {user.contact ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        <span className="truncate text-foreground">{user.contact}</span>
                      </div>
                    ) : null}
                    {user.membershipStartDate ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span className="truncate text-foreground">
                          Member since {String(user.membershipStartDate)}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-lg border bg-background p-3">
                    <p className="text-xs font-medium text-muted-foreground">Address</p>
                    <p className="mt-1 text-sm">
                      {[user.address?.street, user.address?.city, user.address?.province]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {[user.address?.postalCode, user.address?.country].filter(Boolean).join(", ")}
                    </p>
                  </div>
                </CardContent>
              ) : null}
            </Card>

            <Card className="border-primary/10 bg-card/70 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Quick actions</CardTitle>
                <CardDescription>Order tags and manage membership.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  variant="outline"
                  onClick={() => navigate("/prices")}
                  className="w-full justify-start gap-2 bg-card/60 backdrop-blur supports-[backdrop-filter]:bg-card/50 hover:bg-card/80"
                >
                  <Tag className="h-4 w-4" />
                  View pricing
                </Button>
                <Button
                  onClick={() => navigate("/select-tag/standard")}
                  disabled={!canOrderTags}
                  className="w-full justify-start gap-2 shadow-sm hover:shadow"
                >
                  <Tag className="h-4 w-4" />
                  Order tags (R{PRICING.TAG_ONCE_OFF_RANDS} each)
                </Button>

                <div className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
                  <p className="flex items-center gap-2 font-medium text-foreground">
                    <PawPrint className="h-4 w-4" />
                    Add a pet before ordering tags
                  </p>
                  <p className="mt-1">
                    Tags are once-off. If a pet isn&apos;t subscribed yet, checkout will also start membership (R{PRICING.MEMBERSHIP_MONTHLY_RANDS}/month) in the same payment.
                  </p>
                  {!canOrderTags ? (
                    <p className="mt-2">You don&apos;t have any pets yet—add one to continue.</p>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card className="border-primary/10 bg-card/70 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/60">
                <CardHeader className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <CardDescription>Total pets</CardDescription>
                    <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-primary/10 ring-1 ring-primary/10">
                      <PawPrint className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-3xl">{visiblePets.length}</CardTitle>
                </CardHeader>
              </Card>
              <Card className="border-primary/10 bg-card/70 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/60">
                <CardHeader className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <CardDescription>Membership active</CardDescription>
                    <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-primary/10 ring-1 ring-primary/10">
                      <Tag className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-3xl">{subscribedPets.length}</CardTitle>
                </CardHeader>
              </Card>
              <Card className="border-primary/10 bg-card/70 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/60">
                <CardHeader className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <CardDescription>Dogs</CardDescription>
                    <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-primary/10 ring-1 ring-primary/10">
                      <PawPrint className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-3xl">{dogs.length}</CardTitle>
                </CardHeader>
              </Card>
              <Card className="border-primary/10 bg-card/70 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/60">
                <CardHeader className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <CardDescription>Cats</CardDescription>
                    <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-primary/10 ring-1 ring-primary/10">
                      <PawPrint className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-3xl">{cats.length}</CardTitle>
                </CardHeader>
              </Card>
            </div>

            <Card className="border-primary/10 bg-card/70 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Your pets</CardTitle>
                <CardDescription>View, edit, and order tags.</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="all">
                  <div className="overflow-x-auto pb-1">
                    <TabsList className="w-max justify-start bg-muted/40">
                      <TabsTrigger className="mr-4 btn outline-1" value="all">All</TabsTrigger>
                      <TabsTrigger  className="mr-4 btn "value="dogs">Dogs</TabsTrigger>
                      <TabsTrigger  className="btn" value="cats">Cats</TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent value="all">
                    <PetListSection
                      title="All pets"
                      showHeader={false}
                      pets={visiblePets}
                      loading={loading}
                      user={user}
                      handleViewDetails={handleViewDetails}
                      handleEditClick={handleEditClick}
                      handleDeleteClick={handleDeleteClick}
                    />
                  </TabsContent>

                  <TabsContent value="dogs">
                    <PetListSection
                      title="Dogs"
                      showHeader={false}
                      pets={dogs}
                      loading={loading}
                      user={user}
                      handleViewDetails={handleViewDetails}
                      handleEditClick={handleEditClick}
                      handleDeleteClick={handleDeleteClick}
                    />
                  </TabsContent>

                  <TabsContent value="cats">
                    <PetListSection
                      title="Cats"
                      showHeader={false}
                      pets={cats}
                      loading={loading}
                      user={user}
                      handleViewDetails={handleViewDetails}
                      handleEditClick={handleEditClick}
                      handleDeleteClick={handleDeleteClick}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>

        {!isEditMode && (
          <AddPetModal
            showModal={showModal}
            closeModal={handleCloseModal}
            refreshPets={refreshPets}
          />
        )}

        {isEditMode && (
          <EditPetModal
            show={showModal}
            formData={formData}
            handleChange={handleChange}
            handleClose={handleCloseModal}
            handleSave={handleSaveChanges}
            refreshPets={refreshPets}
          />
        )}

        {showDetailsModal && selectedPet && (
          <PetDetailsModal
            show={showDetailsModal}
            handleClose={handleCloseDetailsModal}
            pet={selectedPet}
          />
        )}

        <DeleteConfirmationModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          handleConfirm={confirmDeletePet}
          isDeleting={isDeleting}
          deletionSuccess={false}
          refreshPets={refreshPets}
        />
      </div>
    </main>
  );
};

export default Dashboard;
